import { css } from '@emotion/core'
import {
  Cell,
  Grid,
  Heading,
  HFlow,
  Icon,
  isEmpty,
  Modal,
  ModalBody,
  ModalFooter,
  ModalFooterButton,
  ModalHeader,
  Tooltip,
  VFlow,
} from 'bold-ui'
import { Divider } from 'components/divider/Divider'
import { createDecorator, Form, FormRenderProps } from 'components/form'
import { DateRangeField } from 'components/form/field/daterange/DateRangeField'
import { RacaCorSelectField } from 'components/form/field/select/RacaCorSelectField'
import { AcompanhamentoCondicaoSaudeTematicaEnum } from 'graphql/types.generated'
import React, { Fragment, useCallback } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import { metaPath } from 'util/metaPath'
import { matchesWithPartial } from 'util/object'
import { FaixaEtariaFilterGroup } from 'view/acompanhamento/components/FaixaEtariaFilterGroup'
import { SexoIdentidadeGeneroButtonGroup } from 'view/acompanhamento/components/SexoIdentidadeGeneroButtonGroup'

import { acompanhamentoCondicaoSaudeFiltrosAvancadosCalculations } from '../../calculator-acompanhamentoCondicaoSaude'
import {
  AcompanhamentoCondicaoSaudeFiltrosAvancadosModel,
  defaultFilterAcompanhamentoCondicaoSaudeByTematica,
  IvcfFilterModel,
  TipoVulnerabilidadeEnum,
} from '../../model/model-acompanhamentoCondicaoSaude'
import { acompanhamentoCondicaoSaudeFiltrosAvancadosValidator } from '../../validator-acompanhamentoCondicaoSaude'
import { AcompanhamentoCondicaoSaudeCondicaoMaternalRadioGroupField } from './AcompanhamentoCondicaoSaudeCondicaoMaternalRadioGroupField'
import { AcompanhamentoCondicaoSaudeProblemasCondicoesFilter } from './AcompanhamentoCondicaoSaudeProblemasCondicoesFilter'
import { IvcfFilterSection } from './IvcfFilterSection'

export const FILTROS_AVANCADOS_URL = '/filtros-avancados'

const meta = metaPath<AcompanhamentoCondicaoSaudeFiltrosAvancadosModel>()

const decorators = [createDecorator(...acompanhamentoCondicaoSaudeFiltrosAvancadosCalculations(meta))]

interface AcompanhamentoCondicaoSaudeFiltrosAvancadosModalProps {
  now: Date
  onSubmit(values: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel): void
  initialValues: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel
  tematica: AcompanhamentoCondicaoSaudeTematicaEnum
  isAbleToBuscar: boolean
}

export const AcompanhamentoCondicaoSaudeFiltrosAvancadosModal = (
  props: AcompanhamentoCondicaoSaudeFiltrosAvancadosModalProps
) => {
  const { now, onSubmit, initialValues, tematica, isAbleToBuscar } = props

  const history = useHistory()
  const match = useRouteMatch()
  const styles = createStyles()

  const handleSubmit = useCallback(
    (values: AcompanhamentoCondicaoSaudeFiltrosAvancadosModel) => {
      isAbleToBuscar && onSubmit(values)
      history.push(match.url.replace(FILTROS_AVANCADOS_URL, ''))
    },
    [isAbleToBuscar, onSubmit, history, match]
  )

  const handleClose = useCallback(() => {
    history.push(match.url.replace(FILTROS_AVANCADOS_URL, ''))
  }, [history, match])

  const renderForm = ({
    invalid,
    form,
    values,
    handleSubmit: handleSubmitInternal,
  }: FormRenderProps<AcompanhamentoCondicaoSaudeFiltrosAvancadosModel>) => {
    const naoPossuiGrupoOuCondicoes = isEmpty(values?.grupoCondicao) && isEmpty(values?.problemasCondicoes)
    const podeChecarCondicoesAtivas = !values || naoPossuiGrupoOuCondicoes

    const defaultValue = defaultFilterAcompanhamentoCondicaoSaudeByTematica[tematica].filtrosAvancados

    return (
      <Modal open size='large' onClose={handleClose} closeOnBackdropClick>
        <ModalHeader title='Filtros avançados' />
        <ModalBody>
          <VFlow>
            <Grid gapVertical={0}>
              <AcompanhamentoCondicaoSaudeProblemasCondicoesFilter
                permission={Permissions.acompanhamentos.condicoesDeSaude.problemasECondicoes}
                path={meta}
                canCheckApenasProblemasCondicoesAtivas={podeChecarCondicoesAtivas}
                isInsideModal
              />
              {tematica === AcompanhamentoCondicaoSaudeTematicaEnum.GESTANTE_PUERPERA && (
                <Cell size={12}>
                  <Divider style={styles.divider} />
                  <AcompanhamentoCondicaoSaudeCondicaoMaternalRadioGroupField meta={meta.condicaoMaternal} />
                </Cell>
              )}
              <Cell size={12}>
                <Divider style={styles.divider} />
              </Cell>
              <Cell size={9}>
                <SexoIdentidadeGeneroButtonGroup onChange={form.change} meta={meta} />
              </Cell>
              <Cell size={12}>
                <Divider style={styles.divider} />
              </Cell>
              <Cell size={6}>
                <VFlow>
                  <Heading level={3}>Período do último atendimento</Heading>
                  <DateRangeField
                    name={meta.periodoUltimoAtendimento.absolutePath()}
                    // TODO eldorado #916 (bold): corrigir as props startPlaceholder e finalPlaceholder
                    // startPlaceholder='Data inicial'
                    // finalPlaceholder='Data final'
                    maxDate={now}
                  />
                </VFlow>
              </Cell>
              <Cell size={12}>
                <Divider style={styles.divider} />
              </Cell>
              <Cell size={5}>
                <VFlow>
                  <Heading level={3}>Raça/cor</Heading>
                  <RacaCorSelectField name={meta.racaCor} />
                </VFlow>
              </Cell>
              <Cell size={12}>
                <Divider style={styles.divider} />
              </Cell>
              <Cell size={12}>
                <VFlow>
                  <Heading level={3}>Faixa etária</Heading>
                  <FaixaEtariaFilterGroup
                    change={form.change}
                    meta={meta}
                    faixaEtariaFilter={values.tipoFaixaEtaria}
                    tipoVulnerabilidade={
                      tematica === AcompanhamentoCondicaoSaudeTematicaEnum.PESSOA_IDOSA
                        ? TipoVulnerabilidadeEnum.IVCF
                        : undefined
                    }
                    isInsideModal
                  />
                </VFlow>
              </Cell>
              {tematica === AcompanhamentoCondicaoSaudeTematicaEnum.PESSOA_IDOSA && (
                <Fragment>
                  <Cell size={12}>
                    <Divider style={styles.divider} />
                  </Cell>
                  <Cell size={12}>
                    <IvcfFilterSection<IvcfFilterModel> meta={meta.ivcf} />
                  </Cell>
                </Fragment>
              )}
            </Grid>
          </VFlow>
        </ModalBody>
        <ModalFooter>
          <HFlow justifyContent='space-between'>
            <ModalFooterButton
              onClick={() => form.reset(defaultValue)}
              disabled={matchesWithPartial(values, defaultValue)}
            >
              <Icon icon='redo' style={styles.icon} />
              Voltar para padrão
            </ModalFooterButton>
            <HFlow>
              <ModalFooterButton onClick={handleClose}>Cancelar</ModalFooterButton>
              <Tooltip text={invalid ? 'Existem campos preenchidos de forma incorreta.' : undefined}>
                <ModalFooterButton kind='primary' disabled={invalid} onClick={handleSubmitInternal}>
                  Aplicar
                </ModalFooterButton>
              </Tooltip>
            </HFlow>
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Form<AcompanhamentoCondicaoSaudeFiltrosAvancadosModel>
      render={renderForm}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      decorators={decorators}
      validate={acompanhamentoCondicaoSaudeFiltrosAvancadosValidator}
    />
  )
}

export const createStyles = () => ({
  icon: css`
    margin-right: 0.5rem;
  `,
  divider: css`
    margin: 1rem 0;
  `,
})
